/**
 * Real `SiteAPI` implementation backed by the HTTP client (ADR-0019 §3.1).
 *
 * Wire mapping:
 *   - list:        GET   /v1/sites             200 + `{ items, next_cursor }`
 *   - get:         GET   /v1/sites/:site_id    200 + `{ site }`; 404 → null
 *   - create:      POST  /v1/sites             201 + `{ site, site_key_shown_once }`;
 *                                              409 → ApiError('conflict') re-thrown
 *   - update:      PATCH /v1/sites/:site_id    200 + `{ site }` (ADR-0023, ADR-0144);
 *                                              Phase-1 allow-list is `{ name }` only;
 *                                              403 (reviewer/viewer) + 404 propagate
 *                                              as typed `ApiError` for the view layer
 *   - enableSampleTraffic: client-side toggle; resolves void (no fetch)
 *
 * The `create()` response carries the raw `site_key` once (ADR-0010 §3.2);
 * the onboarding `InstallStep` reads it from the active site, renders it
 * into the embed snippet, then drops it. `Site` already carries an
 * optional `site_key` field; we return the full shape including the
 * raw key.
 *
 * Pagination — `list()` returns the first page (50 sites max per
 * ADR-0010 §3.1). Surfacing `next_cursor` requires a dashboard UI
 * design that doesn't exist; deferring to a future ADR is consistent
 * with §1.4's "absorb the gap inside the client" pattern.
 */

import type { InstallStatus, InterestProtocol, Site, SiteId } from '@sill/types';
import type {
  AddSiteInput,
  AgenticCommerceAccountSession,
  AgenticCommerceAvailability,
  AgenticCommerceMarkEnabledResult,
  AllowedOriginsResult,
  CloudflareDisconnectResult,
  CloudflareIntegrationStatus,
  CloudflareRedeployResult,
  InstallUpdatesResponse,
  ConnectorStatus,
  ConnectorStatusKindFilter,
  DetectPlatformResult,
  InstallCoverage,
  SetStripeRailInput,
  ShopifyEmbedInstallRetryResult,
  ShopifyStorefrontMintRetryResult,
  SiteAPI,
  SiteInstallsResponse,
  InstallRevokeResponse,
  SitesListOptions,
  StripeAvailability,
  StripeRailMutationResult,
  StripeRailState,
  UnregisteredOriginsResult,
  WooCommerceConnectInput,
  WooCommerceConnectResult,
  SlackWebhookStatus,
  SlackWebhookSaveInput,
  SlackWebhookSaveResult,
  SlackWebhookTestResult,
} from '@sill/types/api';

import { ApiError, type HttpClient } from './http-client.js';

export interface RealSitesDeps {
  readonly http: HttpClient;
}

interface SitesListResponse {
  items: Site[];
  next_cursor: string | null;
}

interface SiteResponse {
  site: Site;
}

interface CreateSiteResponse {
  site: Site & { site_key: string };
  site_key_shown_once: true;
}

export function createRealSitesApi(deps: RealSitesDeps): SiteAPI {
  return {
    async list(options?: SitesListOptions): Promise<Site[]> {
      // ADR-0051 §2.2 — `include_deleted=true` widens the response to
      // include soft-deleted sites so the dashboard's "Show deleted
      // sites" toggle can route the merchant to archived audit
      // records. Default (omitted / false) preserves today's contract.
      // Tenant scoping is unchanged on the server (RLS + tenant tx);
      // `include_deleted` cannot widen across accounts.
      const path = options?.include_deleted === true
        ? '/v1/sites?include_deleted=true'
        : '/v1/sites';
      const res = await deps.http.request<SitesListResponse>({
        method: 'GET',
        path,
      });
      if (res === undefined) {
        throw new ApiError('bad_response', null);
      }
      return res.items;
    },

    async get(id: SiteId): Promise<Site | null> {
      try {
        const res = await deps.http.request<SiteResponse>({
          method: 'GET',
          path: `/v1/sites/${encodeURIComponent(id)}`,
        });
        if (res === undefined) {
          throw new ApiError('bad_response', null);
        }
        return res.site;
      } catch (err) {
        // 404 → null. ADR-0010 §3.2: RLS denials also manifest as 404
        // (the SELECT under RLS returns zero rows). The dashboard's
        // response is identical in both cases — `get()` returns null.
        if (err instanceof ApiError && err.kind === 'not_found') {
          return null;
        }
        throw err;
      }
    },

    async create(input: AddSiteInput): Promise<Site> {
      const res = await deps.http.request<CreateSiteResponse>({
        method: 'POST',
        path: '/v1/sites',
        body: input,
      });
      if (res === undefined) {
        throw new ApiError('bad_response', null);
      }
      // The raw site_key surfaces here and ONLY here (ADR-0010 §3.2).
      // Onboarding's InstallStep renders it into the embed snippet then
      // drops it — no subsequent endpoint returns it.
      return res.site;
    },

    async verify(id: SiteId): Promise<Site> {
      const res = await deps.http.request<SiteResponse>({
        method: 'POST',
        path: `/v1/sites/${encodeURIComponent(id)}/verify`,
      });
      if (res === undefined) {
        throw new ApiError('bad_response', null);
      }
      return res.site;
    },

    async getInstallStatus(id: SiteId): Promise<InstallStatus> {
      const res = await deps.http.request<InstallStatus>({
        method: 'GET',
        path: `/v1/sites/${encodeURIComponent(id)}/install-status`,
      });
      if (res === undefined) {
        throw new ApiError('bad_response', null);
      }
      return res;
    },

    async getInstallCoverage(id: SiteId): Promise<InstallCoverage> {
      const res = await deps.http.request<InstallCoverage>({
        method: 'GET',
        path: `/v1/sites/${encodeURIComponent(id)}/install-coverage`,
      });
      if (res === undefined) {
        throw new ApiError('bad_response', null);
      }
      return res;
    },

    async edgeResync(id: SiteId): Promise<Site> {
      const res = await deps.http.request<SiteResponse>({
        method: 'POST',
        path: `/v1/sites/${encodeURIComponent(id)}/edge-resync`,
      });
      if (res === undefined) {
        throw new ApiError('bad_response', null);
      }
      return res.site;
    },

    async recordInterest(id: SiteId, protocol: InterestProtocol): Promise<void> {
      await deps.http.request<{ ok: true }>({
        method: 'POST',
        path: `/v1/sites/${encodeURIComponent(id)}/interest`,
        body: { protocol },
      });
    },

    async rotateKey(id: SiteId): Promise<Site & { site_key: string }> {
      const res = await deps.http.request<CreateSiteResponse>({
        method: 'POST',
        path: `/v1/sites/${encodeURIComponent(id)}/rotate-key`,
      });
      if (res === undefined) {
        throw new ApiError('bad_response', null);
      }
      // Same shown-once contract as create — the raw site_key surfaces
      // here and nowhere else; the SiteProvider's sessionStorage cache
      // is the only thing that keeps it visible across reloads.
      return res.site;
    },

    async update(id: SiteId, patch: Partial<Site>): Promise<Site> {
      // ADR-0023 + ADR-0160 — PATCH /v1/sites/:site_id, a partial update over
      // the server-mutable fields: `name` and/or `display_time_zone` (the
      // per-site display-zone override). An empty string for the zone means
      // "clear → inherit the account default" (the server's validateTimeZone
      // treats '' as a clear, so no `null` needs to cross Partial<Site>).
      // Other transitions (status, policy_version, region) live in dedicated
      // endpoints. Reject an empty patch client-side so the view layer can
      // surface a useful error without a round trip.
      // ADR-0186 S5 — `domain` is now PATCH-able while the site is
      // pending_proof (the server enforces the lifecycle gate + returns
      // 409 after proof). Editing it re-grounds the scan server-side.
      // ADR-0206 Slice 4 — `llms_description` and `llms_custom_sections`
      // are optional per-site overrides for the Sill-hosted llms.txt.
      // Empty string on either is the wire signal to CLEAR the override
      // (mirror of display_time_zone's empty-clears posture).
      const body: {
        name?: string;
        display_time_zone?: string;
        domain?: string;
        llms_description?: string;
        llms_custom_sections?: string;
      } = {};
      if (patch.name !== undefined) body.name = patch.name;
      if (patch.display_time_zone !== undefined) {
        body.display_time_zone = patch.display_time_zone;
      }
      if (patch.domain !== undefined) body.domain = patch.domain;
      if (patch.llms_description !== undefined) {
        body.llms_description = patch.llms_description;
      }
      if (patch.llms_custom_sections !== undefined) {
        body.llms_custom_sections = patch.llms_custom_sections;
      }
      if (
        body.name === undefined
        && body.display_time_zone === undefined
        && body.domain === undefined
        && body.llms_description === undefined
        && body.llms_custom_sections === undefined
      ) {
        throw new ApiError('bad_request', null);
      }
      // Errors propagate as-is — the http client already maps a 404 to
      // ApiError('not_found', …) and the view layer surfaces a toast. A
      // not-found here could be a stale list (the site was deleted from
      // another tab) or a cross-tenant attempt (RLS returns 404 by
      // ADR-0010 §3.2).
      const res = await deps.http.request<SiteResponse>({
        method: 'PATCH',
        path: `/v1/sites/${encodeURIComponent(id)}`,
        body,
      });
      if (res === undefined) {
        throw new ApiError('bad_response', null);
      }
      return res.site;
    },

    async enableSampleTraffic(_id: SiteId, _enabled: boolean): Promise<void> {
      // Sample-traffic mode is a client-side dashboard toggle (ADR-0003
      // §"Sample-traffic mode"); there's no server concept yet. The
      // method is a no-op so existing callers in
      // `<SampleTrafficProvider />` and `<DoneStep />` keep working
      // transparently.
      return;
    },

    async detectPlatform(id: SiteId): Promise<DetectPlatformResult> {
      // ADR-0037 §1.1 — fingerprint the verified merchant domain. The
      // wire shape mirrors the server's response (DetectPlatformResult)
      // without an envelope; the route is `GET`-based since the
      // detection is idempotent.
      const res = await deps.http.request<DetectPlatformResult>({
        method: 'GET',
        path: `/v1/sites/${encodeURIComponent(id)}/detect-platform`,
      });
      if (res === undefined) {
        throw new ApiError('bad_response', null);
      }
      return res;
    },

    async getConnectorStatus(
      id: SiteId,
      kind?: ConnectorStatusKindFilter,
    ): Promise<ConnectorStatus> {
      // ADR-0037 §"Open questions" — additive read for the dashboard's
      // ConnectBackendStep + post-OAuth poll. A 404 means no integration
      // row exists yet (the merchant has not connected anything); we
      // return the baseline disconnected state so the view layer
      // doesn't have to branch.
      //
      // ADR-0038 §4 payment-rail connect — optional `kind` filter so the
      // dashboard can read the (site_id, kind='stripe') row on a site
      // that ALSO has Shopify connected. Omitting `kind` preserves the
      // exact pre-filter behavior — the Shopify onboarding path is
      // byte-for-byte unchanged.
      const query = kind === undefined ? '' : `?kind=${encodeURIComponent(kind)}`;
      try {
        const res = await deps.http.request<ConnectorStatus>({
          method: 'GET',
          path: `/v1/sites/${encodeURIComponent(id)}/connector${query}`,
        });
        if (res === undefined) {
          throw new ApiError('bad_response', null);
        }
        return res;
      } catch (err) {
        if (err instanceof ApiError && err.kind === 'not_found') {
          return { kind: null, status: null, wired_skills: [] };
        }
        throw err;
      }
    },

    async getStripeAvailability(id: SiteId): Promise<StripeAvailability> {
      // ADR-0038 §4 — lightweight boolean probe so the
      // ConnectStripeSection can render the dormant amber tile (rather
      // than letting the merchant click into a raw 503 page) when the
      // deploy hasn't provisioned the Stripe Connect OAuth secrets yet.
      // 404 → false (the caller doesn't own the site, OR the site
      // truly doesn't exist; either way the dashboard's response is
      // "treat as unavailable").
      try {
        const res = await deps.http.request<StripeAvailability>({
          method: 'GET',
          path: `/v1/sites/${encodeURIComponent(id)}/stripe-availability`,
        });
        if (res === undefined) {
          throw new ApiError('bad_response', null);
        }
        return res;
      } catch (err) {
        if (err instanceof ApiError && err.kind === 'not_found') {
          return { available: false };
        }
        throw err;
      }
    },

    async getAgenticCommerceAvailability(
      id: SiteId,
    ): Promise<AgenticCommerceAvailability> {
      // ADR-0141 Slice 1 (Capability A) — probe whether the platform-
      // side capability gate is ON and whether the merchant has
      // connected Stripe + completed the embedded wizard. Default
      // (gate-OFF) returns `{available:false}` — the dashboard
      // renders the "not yet available" tile. 404 → `available:false`
      // (cross-tenant or unknown site; same observable shape).
      try {
        const res = await deps.http.request<AgenticCommerceAvailability>({
          method: 'GET',
          path: `/v1/sites/${encodeURIComponent(id)}/integrations/stripe/agentic-commerce/availability`,
        });
        if (res === undefined) {
          throw new ApiError('bad_response', null);
        }
        return res;
      } catch (err) {
        if (err instanceof ApiError && err.kind === 'not_found') {
          return { available: false };
        }
        throw err;
      }
    },

    async mintAgenticCommerceAccountSession(
      id: SiteId,
    ): Promise<AgenticCommerceAccountSession> {
      // ADR-0141 Slice 1 (Capability A) — POST to mint a Stripe
      // account_session. Owner/admin only — the server enforces 403
      // for reviewer/viewer. The `client_secret` returned IS browser-
      // reachable by Connect SDK design; the platform secret NEVER
      // leaves the server. Errors propagate as ApiError; the view
      // layer surfaces:
      //   - 403 role_insufficient → "Permission needed"
      //   - 404 integration_missing → "Connect Stripe first"
      //   - 503 agentic_commerce_not_enabled → dormant tile
      //   - 502 bad_gateway → "Stripe is unavailable, try again"
      //   - 429 rate_limited → "Try again in a moment"
      const res = await deps.http.request<AgenticCommerceAccountSession>({
        method: 'POST',
        path: `/v1/sites/${encodeURIComponent(id)}/integrations/stripe/agentic-commerce/account-session`,
      });
      if (res === undefined) {
        throw new ApiError('bad_response', null);
      }
      return res;
    },

    async markAgenticCommerceEnabled(
      id: SiteId,
    ): Promise<AgenticCommerceMarkEnabledResult> {
      // ADR-0141 Slice 1 (Capability A) — POST to record the merchant's
      // browser-reported success from Stripe's embedded wizard. Owner/
      // admin only. Idempotent — calling twice is harmless (the column
      // overwrites with now() on each call). 503 surfaces when the
      // capability gate is OFF; 404 when the merchant disconnected
      // Stripe between the wizard mount and the success event.
      const res = await deps.http.request<AgenticCommerceMarkEnabledResult>({
        method: 'POST',
        path: `/v1/sites/${encodeURIComponent(id)}/integrations/stripe/agentic-commerce/mark-enabled`,
      });
      if (res === undefined) {
        throw new ApiError('bad_response', null);
      }
      return res;
    },

    async getStripeRail(id: SiteId): Promise<StripeRailState> {
      // ADR-0038 §9 + §13 — read the per-site payment-rail state.
      // Mirrors the wire shape from the route handler 1:1. Errors
      // propagate as `ApiError` (the view layer surfaces them as
      // toasts); a 404 is a real signal (the site does not exist
      // for this caller) and is NOT collapsed to a baseline here
      // — the caller decides what to render.
      const res = await deps.http.request<StripeRailState>({
        method: 'GET',
        path: `/v1/sites/${encodeURIComponent(id)}/stripe-rail`,
      });
      if (res === undefined) {
        throw new ApiError('bad_response', null);
      }
      return res;
    },

    async delete(id: SiteId): Promise<void> {
      // Soft-delete on the server (audit-chain FKs are `ON DELETE
      // RESTRICT`; CLAUDE.md §3). Wire: DELETE /v1/sites/:site_id.
      // Success → 204 → http-client returns undefined → resolve void.
      // 403 (reviewer/viewer) and 404 (cross-tenant / unknown /
      // already-deleted) propagate as `ApiError`; the view layer
      // surfaces a toast and refreshes the list on 404 to converge
      // with another tab that may have already deleted the row.
      await deps.http.request<undefined>({
        method: 'DELETE',
        path: `/v1/sites/${encodeURIComponent(id)}`,
      });
    },

    async retryShopifyStorefrontTokenMint(
      id: SiteId,
    ): Promise<ShopifyStorefrontMintRetryResult> {
      // ADR-0181 §Handoff — retry the Shopify Storefront-Access-Token
      // mint. Server re-runs `storefrontAccessTokenCreate` with the
      // stored Admin credential, encrypts the minted token into a new
      // `connector_credential` row (`kind = 'shopify_storefront'`),
      // and persists the outcome to
      // `integration.storefront_consent_status`. Typed errors:
      //   - 429 (rate_limited) — view surfaces a "try again" toast.
      //   - 409 (conflict) — shopify no longer connected; view
      //     surfaces a "reconnect" prompt.
      //   - 503 — origin Shopify OAuth not configured (dev / staging).
      const res = await deps.http.request<ShopifyStorefrontMintRetryResult>({
        method: 'POST',
        path: `/v1/sites/${encodeURIComponent(id)}/connector/shopify/mint-storefront-token`,
      });
      if (res === undefined) {
        throw new ApiError('bad_response', null);
      }
      return res;
    },

    async retryShopifyEmbedInstall(
      id: SiteId,
    ): Promise<ShopifyEmbedInstallRetryResult> {
      // ADR-0053 §4 — retry the Shopify embed ScriptTag install. The
      // server re-runs `installEmbedScriptTag` with the stored
      // credential and persists the outcome to
      // `integration.embed_install_status`. Typed errors propagate:
      //   - 429 (rate_limited) — the view layer surfaces a "try again"
      //     toast.
      //   - 409 (conflict) — shopify is no longer connected; the view
      //     layer surfaces a "reconnect" prompt.
      //   - 503 — origin Shopify OAuth not configured (dev / staging).
      const res = await deps.http.request<ShopifyEmbedInstallRetryResult>({
        method: 'POST',
        path: `/v1/sites/${encodeURIComponent(id)}/connector/shopify/install-embed`,
      });
      if (res === undefined) {
        throw new ApiError('bad_response', null);
      }
      return res;
    },

    async connectWooCommerce(
      id: SiteId,
      input: WooCommerceConnectInput,
    ): Promise<WooCommerceConnectResult> {
      // ADR-0170 §2 — WooCommerce manual-key connect. Session-cookie
      // authed (same as every other bag call). The server performs the
      // full posture: role gate, verified-domain L1 SSRF gate, live key
      // probe (SSRF-hardened egress), KMS envelope encrypt, integration
      // upsert, SITE_SKILLS re-publish. Typed errors propagate via
      // `ApiError`; the view layer branches on `err.status` +
      // `err.server_reason` to render the precise merchant-facing
      // message:
      //
      //   - 400 bad_request (body_invalid, key_format_invalid)
      //   - 400 site_url_rejected (invalid_url / unsupported_scheme /
      //     userinfo_forbidden / ip_literal_forbidden / port_forbidden /
      //     off_domain / query_forbidden)
      //   - 400 connect_probe_failed (invalid_credentials / not_woocommerce)
      //   - 502 connect_probe_failed (site_unreachable)
      //   - 403 forbidden (role_insufficient)
      //   - 404 not_found
      //   - 429 rate_limited (Retry-After header)
      //   - 503 service_unavailable (connector_kms_not_configured)
      //
      // The secret pair is sent verbatim in the JSON body — TLS + the
      // cross-origin fetch's `credentials: 'include'` posture from the
      // shared http-client covers the wire. The server wipes the
      // plaintext immediately after envelope encrypt and never echoes
      // it back.
      const res = await deps.http.request<WooCommerceConnectResult>({
        method: 'POST',
        path: `/v1/sites/${encodeURIComponent(id)}/connector/woocommerce`,
        body: input,
      });
      if (res === undefined) {
        throw new ApiError('bad_response', null);
      }
      return res;
    },

    async disconnectConnector(
      id: SiteId,
      kind: ConnectorStatusKindFilter,
    ): Promise<void> {
      // ADR-0054 §1.1 — merchant-driven connector disconnect. Server
      // performs:
      //   1. Owner/admin role check (reviewer/viewer → 403).
      //   2. (Shopify only) best-effort ScriptTag removal BEFORE the
      //      credential revoke (fail-soft).
      //   3. `revokeCredential` + `UPDATE integration.status='disconnected'`
      //      + (Stripe only) `UPDATE site.stripe_rail_enabled=false` all
      //      under one tenant tx.
      //   4. `publishSiteSkills` re-publish so the edge stops routing
      //      wired-skill dispatch through the revoked credential.
      //
      // Idempotent: a second disconnect is 204. Typed errors propagate
      // via `ApiError`:
      //   - 400 (bad_request, kind_required / kind_invalid / site_id_invalid)
      //   - 403 (forbidden, role_insufficient)
      //   - 404 (not_found — cross-tenant, soft-deleted, or unknown)
      await deps.http.request<undefined>({
        method: 'DELETE',
        path:
          `/v1/sites/${encodeURIComponent(id)}/connector`
          + `?kind=${encodeURIComponent(kind)}`,
      });
    },

    async getSlackWebhookStatus(id: SiteId): Promise<SlackWebhookStatus> {
      // ADR-0098 v2 — fetch slack webhook configured status. 404 if site
      // does not exist or is cross-tenant (RLS). Never returns the URL.
      const res = await deps.http.request<SlackWebhookStatus>({
        method: 'GET',
        path: `/v1/sites/${encodeURIComponent(id)}/connector/slack-webhook`,
      });
      if (res === undefined) {
        throw new ApiError('bad_response', null);
      }
      return res;
    },

    async saveSlackWebhook(
      id: SiteId,
      input: SlackWebhookSaveInput,
    ): Promise<SlackWebhookSaveResult> {
      // ADR-0098 v2 — store or rotate the Slack webhook URL (KMS-encrypted).
      //
      // Typed errors propagate via ApiError:
      //   - 400 bad_request (body_invalid, url_required, url_must_be_slack_webhook)
      //   - 403 forbidden (role_insufficient)
      //   - 404 not_found
      //   - 429 rate_limited
      //   - 503 service_unavailable (connector_kms_not_configured)
      const res = await deps.http.request<SlackWebhookSaveResult>({
        method: 'PUT',
        path: `/v1/sites/${encodeURIComponent(id)}/connector/slack-webhook`,
        body: input,
      });
      if (res === undefined) {
        throw new ApiError('bad_response', null);
      }
      return res;
    },

    async deleteSlackWebhook(id: SiteId): Promise<void> {
      // ADR-0098 v2 — revoke the Slack webhook credential. Idempotent;
      // revoking when not configured is a 200 no-op (server returns
      // {disconnected:true}). We discard the body — void is fine.
      await deps.http.request<undefined>({
        method: 'DELETE',
        path: `/v1/sites/${encodeURIComponent(id)}/connector/slack-webhook`,
      });
    },

    async testSlackWebhook(id: SiteId): Promise<SlackWebhookTestResult> {
      // ADR-0098 v2 — post a grey-bar test Block Kit message to the
      // stored webhook URL. 404 when not configured. 502 on Slack error.
      const res = await deps.http.request<SlackWebhookTestResult>({
        method: 'POST',
        path: `/v1/sites/${encodeURIComponent(id)}/connector/slack-webhook/test`,
      });
      if (res === undefined) {
        throw new ApiError('bad_response', null);
      }
      return res;
    },

    async sendSampleSlackSecurityAlert(id: SiteId): Promise<SlackWebhookTestResult> {
      // Coordinator 2026-10-20 follow-up B — post a sample got-through
      // Block Kit alert (fictional data: `203.0.113.42`, "Sample alert —
      // not a real attack" header context, buttons link to the Settings
      // page, never an action route). Same role gate, rate limit, and
      // 404/502 shape as /test.
      const res = await deps.http.request<SlackWebhookTestResult>({
        method: 'POST',
        path: `/v1/sites/${encodeURIComponent(id)}/connector/slack-webhook/sample-security-alert`,
      });
      if (res === undefined) {
        throw new ApiError('bad_response', null);
      }
      return res;
    },

    async toggleSlackWebhook(
      id: SiteId,
      enabled: boolean,
    ): Promise<{ enabled: boolean }> {
      // ADR-0098 v2 — pause or resume Slack notifications without disconnecting.
      const res = await deps.http.request<{ enabled: boolean }>({
        method: 'PATCH',
        path: `/v1/sites/${encodeURIComponent(id)}/connector/slack-webhook`,
        body: { enabled },
      });
      if (res === undefined) {
        throw new ApiError('bad_response', null);
      }
      return res;
    },

    async getAgentReadyCloudflareWorker(id: SiteId): Promise<string> {
      // ADR-0188 — the server returns the Worker JS text with
      // application/javascript + Content-Disposition:attachment. The
      // http client has no dedicated raw-text-response method, so we
      // route through `requestBlob` (same 4xx/5xx typed-error mapping
      // as `request`) and read the blob's body as text. Any-role read;
      // credentials ride through session cookie as usual.
      // ADR-0231 — POST: the download mints this Worker's report-signing key
      // (a GET returns an unsigned file and never mints).
      const blob = await deps.http.requestBlob({
        method: 'POST',
        path: `/v1/sites/${encodeURIComponent(id)}/agent-ready/cloudflare-worker`,
      });
      return await blob.text();
    },

    async getAgentReadyWordpressPlugin(id: SiteId): Promise<Blob> {
      // ADR-0188 — the server returns a ZIP archive (the WordPress
      // Upload Plugin surface accepts only `.zip`). The payload is
      // binary; keep it as a Blob and hand it to the browser download
      // helper unchanged so no bytes are mangled through a text decode.
      // ADR-0231 — POST mints the plugin's report-signing key.
      return await deps.http.requestBlob({
        method: 'POST',
        path: `/v1/sites/${encodeURIComponent(id)}/agent-ready/wordpress-plugin`,
      });
    },

    async listSiteInstalls(id: SiteId): Promise<SiteInstallsResponse> {
      // ADR-0231 — install keys (metadata only; secrets never leave Sill
      // except inside a freshly downloaded / deployed Worker or plugin).
      const res = await deps.http.request<SiteInstallsResponse>({
        method: 'GET',
        path: `/v1/sites/${encodeURIComponent(id)}/installs`,
      });
      if (res === undefined) {
        throw new ApiError('bad_response', null);
      }
      return res;
    },

    async revokeInstallKey(id: SiteId, key_id: string): Promise<InstallRevokeResponse> {
      const res = await deps.http.request<InstallRevokeResponse>({
        method: 'POST',
        path: `/v1/sites/${encodeURIComponent(id)}/installs/keys/${encodeURIComponent(key_id)}/revoke`,
      });
      if (res === undefined) {
        throw new ApiError('bad_response', null);
      }
      return res;
    },

    async revokeInstall(id: SiteId, install_id: string): Promise<InstallRevokeResponse> {
      const res = await deps.http.request<InstallRevokeResponse>({
        method: 'POST',
        path: `/v1/sites/${encodeURIComponent(id)}/installs/${encodeURIComponent(install_id)}/revoke`,
      });
      if (res === undefined) {
        throw new ApiError('bad_response', null);
      }
      return res;
    },

    async getCloudflareIntegration(
      id: SiteId,
    ): Promise<CloudflareIntegrationStatus> {
      // ADR-0188 — read the one-click Cloudflare integration state. Any-
      // role read. 404 collapses to the disconnected baseline so the
      // view layer never has to branch on the wire — the merchant sees
      // "Connect & Deploy" whether they've never connected or the
      // server RLS-filtered the row.
      try {
        const res = await deps.http.request<CloudflareIntegrationStatus>({
          method: 'GET',
          path: `/v1/sites/${encodeURIComponent(id)}/integrations/cloudflare`,
        });
        if (res === undefined) {
          throw new ApiError('bad_response', null);
        }
        return res;
      } catch (err) {
        if (err instanceof ApiError && err.kind === 'not_found') {
          return {
            connected: false,
            cf_zone_name: null,
            cf_script_name: null,
            cf_worker_deployed_at: null,
            install_version: null,
            install_update_available: false,
          };
        }
        throw err;
      }
    },

    async connectCloudflare(
      id: SiteId,
      api_token: string,
    ): Promise<CloudflareIntegrationStatus> {
      // ADR-0188 — token-paste connect. The merchant mints a Cloudflare
      // API token in their own dashboard (with the four required scopes)
      // and pastes it into the Settings form; this endpoint validates the
      // token, resolves the zone from the site's verified domain, and
      // deploys the Sill-managed Worker. Typed errors surface via
      // ApiError; the view layer branches on `err.server_reason`
      // (`token_invalid` | `zone_not_found`) to render an inline hint
      // below the input rather than a coral toast.
      const res = await deps.http.request<CloudflareIntegrationStatus>({
        method: 'POST',
        path: `/v1/sites/${encodeURIComponent(id)}/integrations/cloudflare/connect`,
        body: { api_token },
      });
      if (res === undefined) {
        throw new ApiError('bad_response', null);
      }
      return res;
    },

    async redeployCloudflareWorker(
      id: SiteId,
    ): Promise<CloudflareRedeployResult> {
      // ADR-0188 — trigger a fresh Worker deploy on the connected
      // Cloudflare account/zone for this site. Owner/admin only —
      // server enforces 403 for reviewer/viewer. The dashboard follows
      // up with a re-read of `getCloudflareIntegration` so
      // `cf_worker_deployed_at` reflects the new deploy in the UI.
      const res = await deps.http.request<CloudflareRedeployResult>({
        method: 'POST',
        path: `/v1/sites/${encodeURIComponent(id)}/integrations/cloudflare/redeploy`,
      });
      if (res === undefined) {
        throw new ApiError('bad_response', null);
      }
      return res;
    },

    async disconnectCloudflare(
      id: SiteId,
    ): Promise<CloudflareDisconnectResult> {
      // ADR-0188 — revoke Sill's Cloudflare account-token and tear down
      // the deployed Worker for this site. Owner/admin only. Idempotent
      // — disconnecting when not configured returns
      // `{ disconnected: true }` (200) rather than 404.
      const res = await deps.http.request<CloudflareDisconnectResult>({
        method: 'DELETE',
        path: `/v1/sites/${encodeURIComponent(id)}/integrations/cloudflare`,
      });
      if (res === undefined) {
        throw new ApiError('bad_response', null);
      }
      return res;
    },

    async listInstallUpdates(
      id: SiteId,
    ): Promise<InstallUpdatesResponse> {
      // ADR-0239 — merchant-visible redeploy history. Any-role read.
      // A cross-tenant / soft-deleted site_id collapses to an empty
      // list so the view layer never has to branch on the wire; a
      // route-level 404 propagates (the Settings view only renders
      // the section for real sites it's already loaded).
      try {
        const res = await deps.http.request<InstallUpdatesResponse>({
          method: 'GET',
          path: `/v1/sites/${encodeURIComponent(id)}/install-updates`,
        });
        if (res === undefined) {
          throw new ApiError('bad_response', null);
        }
        return res;
      } catch (err) {
        if (err instanceof ApiError && err.kind === 'not_found') {
          return { items: [] };
        }
        throw err;
      }
    },

    async getUnregisteredOrigins(
      id: SiteId,
      since_days?: number,
    ): Promise<UnregisteredOriginsResult> {
      // ADR-0057 §5 — aggregated read of flagged audit_record rows.
      // Tenant-scoped + authed; a cross-tenant site_id returns 200
      // with empty items (RLS deny semantics; the route handler
      // never 404s on this surface — an empty bucket is a valid
      // answer that the dashboard renders as "no flagged origins").
      const query =
        typeof since_days === 'number'
        && Number.isInteger(since_days)
        && since_days > 0
          ? `?since_days=${since_days}`
          : '';
      const res = await deps.http.request<UnregisteredOriginsResult>({
        method: 'GET',
        path: `/v1/sites/${encodeURIComponent(id)}/unregistered-origins${query}`,
      });
      if (res === undefined) {
        throw new ApiError('bad_response', null);
      }
      return res;
    },

    async getAllowedOrigins(id: SiteId): Promise<AllowedOriginsResult> {
      // ADR-0057 §5 — read the per-site allow-list. 404 on cross-tenant
      // or soft-deleted site_id (the SiteCard never calls this for
      // soft-deleted sites; the allow-list settings view does).
      const res = await deps.http.request<AllowedOriginsResult>({
        method: 'GET',
        path: `/v1/sites/${encodeURIComponent(id)}/allowed-origins`,
      });
      if (res === undefined) {
        throw new ApiError('bad_response', null);
      }
      return res;
    },

    async updateAllowedOrigins(
      id: SiteId,
      action: 'add' | 'remove',
      host: string,
    ): Promise<AllowedOriginsResult> {
      // ADR-0057 §5 — PATCH the per-site allow-list. Idempotent on
      // both branches (re-add is a no-op; remove of an absent host is
      // a no-op). 400 on bad host shape; 404 on cross-tenant /
      // soft-deleted site_id.
      const res = await deps.http.request<AllowedOriginsResult>({
        method: 'PATCH',
        path: `/v1/sites/${encodeURIComponent(id)}/allowed-origins`,
        body: { action, host },
      });
      if (res === undefined) {
        throw new ApiError('bad_response', null);
      }
      return res;
    },

    async setStripeRail(
      id: SiteId,
      input: SetStripeRailInput,
    ): Promise<StripeRailMutationResult> {
      // ADR-0038 §13 — flip the per-site payment-rail gate. The
      // server is the source of truth on the fail-closed gates
      // (connected-stripe / stripe-mode / pm_…-shape). The bag
      // forwards typed errors so the view layer can branch on the
      // server's `reason` codes (stripe_not_connected, invalid_payment_method,
      // rail_enable_live_mode_forbidden, role_insufficient). PUT
      // returns the post-mutation state ({ stripe_rail_enabled,
      // test_payment_method_id, stripe_mode }); the dashboard pairs
      // it with a re-read of `getStripeRail` when it needs the
      // `stripe_connected` precondition for a subsequent render.
      const body: SetStripeRailInput = { enabled: input.enabled };
      if (input.test_payment_method_id !== undefined) {
        body.test_payment_method_id = input.test_payment_method_id;
      }
      const res = await deps.http.request<StripeRailMutationResult>({
        method: 'PUT',
        path: `/v1/sites/${encodeURIComponent(id)}/stripe-rail`,
        body,
      });
      if (res === undefined) {
        throw new ApiError('bad_response', null);
      }
      return res;
    },
  };
}
